import { appealService } from '../services/appealService';

const RASTER_IMAGE_EXT = /\.(jpe?g|png|gif|bmp|webp|avif)$/i;
const MAX_CONCURRENT_PREVIEWS = 3;

const previewCache = new Map();
let activePreviews = 0;
const previewQueue = [];

export const isRasterImageName = (fileName = '') => RASTER_IMAGE_EXT.test(fileName);

export const isSafeImageBlob = (blob) => {
  const type = (blob?.type || '').toLowerCase();
  if (!type || type === 'application/octet-stream' || type === 'binary/octet-stream') {
    return true;
  }
  if (type.includes('svg') || type.includes('xml') || type === 'text/html') {
    return false;
  }
  return type.startsWith('image/');
};

const previewKey = (ticketId, attachmentId) => `${ticketId}:${attachmentId}`;

const runWithLimit = (fn) => new Promise((resolve, reject) => {
  const run = async () => {
    activePreviews += 1;
    try {
      resolve(await fn());
    } catch (error) {
      reject(error);
    } finally {
      activePreviews -= 1;
      const next = previewQueue.shift();
      if (next) next();
    }
  };

  if (activePreviews < MAX_CONCURRENT_PREVIEWS) {
    run();
  } else {
    previewQueue.push(run);
  }
});

export const getPreviewObjectUrl = (ticketId, attachment) => {
  const key = previewKey(ticketId, attachment.AttachmentId);
  const cached = previewCache.get(key);
  if (cached) return cached.promise;

  const promise = runWithLimit(() =>
    appealService.downloadAttachment(ticketId, attachment.AttachmentId, attachment.FileName)
  )
    .then((blob) => {
      if (!isSafeImageBlob(blob)) {
        throw new Error('unsupported-image');
      }
      const url = URL.createObjectURL(blob);
      const entry = previewCache.get(key);
      if (entry) entry.url = url;
      return url;
    })
    .catch((error) => {
      previewCache.delete(key);
      throw error;
    });

  previewCache.set(key, { promise, url: null });
  return promise;
};

export const revokeTicketPreviewUrls = (ticketId) => {
  const prefix = `${ticketId}:`;
  Array.from(previewCache.entries()).forEach(([key, entry]) => {
    if (!key.startsWith(prefix)) return;
    if (entry.url) URL.revokeObjectURL(entry.url);
    previewCache.delete(key);
  });
};

export const downloadBlobAsFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadObjectUrl = (url, fileName) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName || 'attachment';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadAppealAttachment = async (ticketId, attachment) => {
  const blob = await appealService.downloadAttachment(
    ticketId,
    attachment.AttachmentId,
    attachment.FileName
  );
  downloadBlobAsFile(blob, attachment.FileName);
};
